import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    ToggleButtonGroup,
    ToggleButton,
    Chip,
    Alert,
    useTheme,
    alpha,
    Tooltip,
    IconButton,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
} from 'recharts';
import {
    SportsCricket as CricketIcon,
    SportsSoccer as FootballIcon,
    SportsBasketball as BasketballIcon,
    People as PeopleIcon,
    Sync as SyncIcon,
    Event as EventIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../../services/api';

// ── Types ─────────────────────────────────────────────────────────────────

interface MatchTeamStatsData {
    match_id: number;
    sport: string;
    match_name: string;
    series_name: string;
    start_date: string;
    match_status: string;
    users_created_team: number;
    users_synced_team: number;
    synced_users_by_platform: Record<string, number>;
    total_users: number;
}

interface MatchTeamStatsSummary {
    total_matches: number;
    total_users_created: number;
    total_users_synced: number;
    sport_breakdown: Record<string, {
        matches: number;
        users_created: number;
        users_synced: number;
    }>;
}

interface MatchTeamStatsResponse {
    matches: MatchTeamStatsData[];
    summary: MatchTeamStatsSummary;
    filters: {
        date_from: string;
        date_to: string;
        sport: string;
    };
}

// ── Platform Colors ───────────────────────────────────────────────────────

const PLATFORM_COLORS: Record<string, string> = {
    dream11: '#E53935',
    my11circle: '#43A047',
    vision11: '#7B1FA2',
    prvc: '#1565C0',
    prvf: '#F57C00',
    prvd: '#00838F',
};

const PLATFORM_LABELS: Record<string, string> = {
    dream11: 'Dream11',
    my11circle: 'My11Circle',
    vision11: 'Vision11',
    prvc: 'PRVC',
    prvf: 'PRVF',
    prvd: 'PRVD',
};

const SPORT_ICONS: Record<string, React.ReactElement> = {
    cricket: <CricketIcon fontSize="small" />,
    football: <FootballIcon fontSize="small" />,
    basketball: <BasketballIcon fontSize="small" />,
};

const SPORT_COLORS: Record<string, string> = {
    cricket: '#43A047',
    football: '#1565C0',
    basketball: '#F57C00',
};

// ── Summary Card ──────────────────────────────────────────────────────────

const SummaryCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
}> = ({ title, value, icon, color, subtitle }) => {
    return (
        <Card
            sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, ${alpha(color, 0.02)} 100%)`,
                border: `1px solid ${alpha(color, 0.15)}`,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 20px ${alpha(color, 0.15)}`,
                },
            }}
        >
            <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                    <Box
                        sx={{
                            backgroundColor: alpha(color, 0.12),
                            borderRadius: '12px',
                            p: 1,
                            mr: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {icon}
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        {title}
                    </Typography>
                </Box>
                <Typography variant="h4" fontWeight={700} color={color}>
                    {value.toLocaleString()}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        {subtitle}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

// ── Custom Bar Chart Tooltip ──────────────────────────────────────────────

const CustomBarTooltip: React.FC<any> = ({ active, payload, label }) => {
    const theme = useTheme();
    if (!active || !payload || payload.length === 0) return null;

    return (
        <Card
            sx={{
                p: 1.5,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: theme.shadows[4],
                maxWidth: 280,
            }}
        >
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                {label}
            </Typography>
            {payload.map((entry: any, index: number) => (
                <Box key={index} display="flex" alignItems="center" gap={1} mb={0.3}>
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '2px',
                            backgroundColor: entry.color,
                            flexShrink: 0,
                        }}
                    />
                    <Typography variant="caption" color="text.secondary" flex={1}>
                        {entry.name}
                    </Typography>
                    <Typography variant="caption" fontWeight={600}>
                        {entry.value?.toLocaleString()}
                    </Typography>
                </Box>
            ))}
        </Card>
    );
};

// ── Match Row ─────────────────────────────────────────────────────────────

const MatchRow: React.FC<{ match: MatchTeamStatsData }> = ({ match }) => {
    const theme = useTheme();

    const startDate = new Date(match.start_date);
    const isUpcoming = startDate > new Date();
    const dateStr = startDate.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
    const timeStr = startDate.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    });

    // Build chart data for this match
    const platformData = Object.entries(match.synced_users_by_platform)
        .filter(([_, count]) => count > 0)
        .sort((a, b) => b[1] - a[1]);

    const barChartData = [
        {
            name: 'Created',
            value: match.users_created_team,
            color: theme.palette.mode === 'dark' ? '#90CAF9' : '#1976D2',
        },
        ...platformData.map(([platform, count]) => ({
            name: PLATFORM_LABELS[platform] || platform,
            value: count,
            color: PLATFORM_COLORS[platform] || '#9E9E9E',
        })),
    ];

    const sportColor = SPORT_COLORS[match.sport] || '#9E9E9E';

    return (
        <Card
            sx={{
                mb: 1.5,
                border: `1px solid ${alpha(sportColor, 0.15)}`,
                transition: 'all 0.2s',
                '&:hover': {
                    borderColor: alpha(sportColor, 0.35),
                    boxShadow: `0 2px 12px ${alpha(sportColor, 0.1)}`,
                },
            }}
        >
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Grid container spacing={2} alignItems="center">
                    {/* Match Info */}
                    <Grid item xs={12} md={4}>
                        <Box display="flex" alignItems="flex-start" gap={1}>
                            <Chip
                                icon={SPORT_ICONS[match.sport]}
                                label={match.sport.charAt(0).toUpperCase() + match.sport.slice(1)}
                                size="small"
                                sx={{
                                    backgroundColor: alpha(sportColor, 0.1),
                                    color: sportColor,
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                    height: 24,
                                }}
                            />
                            {isUpcoming && (
                                <Chip
                                    label="Upcoming"
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    sx={{ height: 24, fontSize: '0.7rem' }}
                                />
                            )}
                        </Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 0.5 }}>
                            {match.match_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {match.series_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {dateStr} • {timeStr}
                        </Typography>
                    </Grid>

                    {/* Bar Visualization */}
                    <Grid item xs={12} md={5}>
                        <Box sx={{ height: 70, width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    layout="vertical"
                                    data={[{ name: match.match_name, ...Object.fromEntries(barChartData.map(d => [d.name, d.value])) }]}
                                    margin={{ top: 0, right: 5, bottom: 0, left: 0 }}
                                >
                                    <XAxis type="number" hide />
                                    <YAxis type="category" dataKey="name" hide />
                                    <RechartsTooltip content={<CustomBarTooltip />} />
                                    {barChartData.map((d) => (
                                        <Bar
                                            key={d.name}
                                            dataKey={d.name}
                                            stackId="a"
                                            fill={d.color}
                                            radius={0}
                                        />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Grid>

                    {/* Counts */}
                    <Grid item xs={12} md={3}>
                        <Box display="flex" gap={2} justifyContent="flex-end">
                            <Tooltip title="Users who created teams">
                                <Box textAlign="center">
                                    <Typography variant="h6" fontWeight={700} color="primary">
                                        {match.users_created_team}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Created
                                    </Typography>
                                </Box>
                            </Tooltip>
                            <Tooltip title="Users who synced teams">
                                <Box textAlign="center">
                                    <Typography variant="h6" fontWeight={700} color="secondary">
                                        {match.users_synced_team}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Synced
                                    </Typography>
                                </Box>
                            </Tooltip>
                            <Tooltip title="Total unique users">
                                <Box textAlign="center">
                                    <Typography variant="h6" fontWeight={700}>
                                        {match.total_users}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Total
                                    </Typography>
                                </Box>
                            </Tooltip>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

// ── Main Component ────────────────────────────────────────────────────────

const MatchTeamStats: React.FC = () => {
    const theme = useTheme();
    const today = new Date();
    const threeDaysLater = new Date(today);
    threeDaysLater.setDate(threeDaysLater.getDate() + 3);

    const [dateFrom, setDateFrom] = useState<Date | null>(today);
    const [dateTo, setDateTo] = useState<Date | null>(threeDaysLater);
    const [sport, setSport] = useState<string>('all');
    const [data, setData] = useState<MatchTeamStatsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params: Record<string, string> = {};
            if (dateFrom) {
                params.date_from = dateFrom.toISOString().split('T')[0];
            }
            if (dateTo) {
                params.date_to = dateTo.toISOString().split('T')[0];
            }
            if (sport !== 'all') {
                params.sport = sport;
            }
            const response = await api.get<MatchTeamStatsResponse>(
                '/fantasy/match-team-stats/',
                { params }
            );
            setData(response.data);
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    }, [dateFrom, dateTo, sport]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Collect all platforms present across all matches for the legend
    const allPlatforms = new Set<string>();
    if (data) {
        data.matches.forEach(m => {
            Object.keys(m.synced_users_by_platform).forEach(p => allPlatforms.add(p));
        });
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
                {/* Filters */}
                <Card sx={{ mb: 3 }}>
                    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                        <Box
                            display="flex"
                            flexWrap="wrap"
                            gap={2}
                            alignItems="center"
                        >
                            <DatePicker
                                label="From"
                                value={dateFrom}
                                onChange={setDateFrom}
                                slotProps={{
                                    textField: { size: 'small', sx: { width: 170 } },
                                }}
                            />
                            <DatePicker
                                label="To"
                                value={dateTo}
                                onChange={setDateTo}
                                slotProps={{
                                    textField: { size: 'small', sx: { width: 170 } },
                                }}
                            />

                            <ToggleButtonGroup
                                value={sport}
                                exclusive
                                onChange={(_, val) => val && setSport(val)}
                                size="small"
                            >
                                <ToggleButton value="all">All</ToggleButton>
                                <ToggleButton value="cricket">
                                    <CricketIcon sx={{ mr: 0.5, fontSize: 18 }} />
                                    Cricket
                                </ToggleButton>
                                <ToggleButton value="football">
                                    <FootballIcon sx={{ mr: 0.5, fontSize: 18 }} />
                                    Football
                                </ToggleButton>
                                <ToggleButton value="basketball">
                                    <BasketballIcon sx={{ mr: 0.5, fontSize: 18 }} />
                                    Basketball
                                </ToggleButton>
                            </ToggleButtonGroup>

                            <Tooltip title="Refresh">
                                <IconButton onClick={fetchData} color="primary" size="small">
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </CardContent>
                </Card>

                {/* Loading */}
                {loading && (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Error */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Data */}
                {data && !loading && (
                    <>
                        {/* Summary Cards */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={4}>
                                <SummaryCard
                                    title="Matches"
                                    value={data.summary.total_matches}
                                    icon={<EventIcon sx={{ color: '#1976D2' }} />}
                                    color="#1976D2"
                                    subtitle="With platform mappings"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <SummaryCard
                                    title="Users Created"
                                    value={data.summary.total_users_created}
                                    icon={<PeopleIcon sx={{ color: '#43A047' }} />}
                                    color="#43A047"
                                    subtitle="Users who generated teams"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <SummaryCard
                                    title="Users Synced"
                                    value={data.summary.total_users_synced}
                                    icon={<SyncIcon sx={{ color: '#7B1FA2' }} />}
                                    color="#7B1FA2"
                                    subtitle="Users who synced to platforms"
                                />
                            </Grid>
                        </Grid>

                        {/* Platform Legend */}
                        {allPlatforms.size > 0 && (
                            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                                <Chip
                                    size="small"
                                    label="Created"
                                    sx={{
                                        backgroundColor: alpha(
                                            theme.palette.mode === 'dark' ? '#90CAF9' : '#1976D2',
                                            0.15
                                        ),
                                        color: theme.palette.mode === 'dark' ? '#90CAF9' : '#1976D2',
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                    }}
                                />
                                {Array.from(allPlatforms).map(platform => (
                                    <Chip
                                        key={platform}
                                        size="small"
                                        label={PLATFORM_LABELS[platform] || platform}
                                        sx={{
                                            backgroundColor: alpha(
                                                PLATFORM_COLORS[platform] || '#9E9E9E',
                                                0.15
                                            ),
                                            color: PLATFORM_COLORS[platform] || '#9E9E9E',
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                        }}
                                    />
                                ))}
                            </Box>
                        )}

                        {/* Match List */}
                        {data.matches.length > 0 ? (
                            <Box>
                                {data.matches.map(match => (
                                    <MatchRow
                                        key={`${match.sport}-${match.match_id}`}
                                        match={match}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <Alert severity="info">
                                No matches with platform mappings found in the selected date range.
                            </Alert>
                        )}
                    </>
                )}
            </Box>
        </LocalizationProvider>
    );
};

export default MatchTeamStats;
